<template>
<div data-component="BaseApp.vue">
	<div class="is-dimmed | md:fixed md:z-[4096] md:inset-0 md:backdrop-blur-sm md:g-black/25" style="display: none" @click="HideDimmed"/>

	<component :is="Module" ref="CurrentModule"/>

	<div
		id="preload-placeholder"
		class="fixed z-[4096] inset-0 flex items-center justify-center bg-white/75 transition-opacity duration-300"
		:class="DisplaySavingScreen ? 'opacity-100 pointer-events-all delay-150' : 'opacity-0 pointer-events-none'"
	>
		<div class="lds-ellipsis">
			<div/>
			<div/>
			<div/>
			<div/>
		</div>
	</div>

	<WebsocketsElement
		v-if="websocketUrl"
		ref="Websockets"
		@show-total-chat-msg-notification="ShowTotalChatMsgNotification"
	/>

	<ToasterElement ref="Toaster"/>

	<PwaDialog v-if="User.CustomerId"/>

	<div v-if="State && CurrentState.GlobalDialog">
		<UnusedCatsDialog/>
	</div>

	<div
		v-if="loggedAs"
		class="fixed bottom-0 left-0 p-2 m-2 text-black z-64 bg-warn"
		style="display: block !important;"
	>
		Zalogowany jako <strong>{{ loggedAs }}</strong>
	</div>
</div>
</template>

<script lang="ts">
import { defineAsyncComponent } from 'vue';
import { mapActions, mapStores, mapWritableState } from 'pinia';
import { useAppStore } from '../stores/app';
import { useUserStore } from '../stores/user';
import { useServicesStore } from '../stores/services';
import { AppModules } from '../types/enums/AppModules';
import ToasterElement from '../globals/components/global/ToasterElement.vue';
import WebsocketsElement from '../globals/components/global/WebsocketsElement.vue';
import PwaDialog from './dialog/PwaDialog.vue';
import StateManager from '../globals/StateManager';
import ServerManager from '../globals/ServerManager';
import Formatters from '../globals/Formatters';
import Validators from '../globals/Validators';
import PushNotifications from '../globals/PushNotifications';
import getDeviceType from '../globals/utilities/GetDeviceType';
import serverFetch from '../globals/ServerFetch';
import { useCustomerOnboardingStore } from '../stores/customerOnboarding';
import {getAppType} from "@utilities/isMobileApp";

export default {
	name: 'BaseApp',
	components: {
		ToasterElement,
		WebsocketsElement,
		PwaDialog,
		CustomerView: defineAsyncComponent(() => import('./CustomerView')),
		PublicView: defineAsyncComponent(() => import('./PublicView')),
		LoginRegister: defineAsyncComponent(() => import('./login-register/LoginRegister')),
		BuyerView: defineAsyncComponent(() => import('../app-buyer/buyer-view/BuyerView')),
		RfpForm: defineAsyncComponent(() => import('../app-buyer/rfp-form/RfpForm')),
		UnusedCatsDialog: defineAsyncComponent(() => import('./dialog/UnusedCatsDialog')),
		BuyerAppProfile: defineAsyncComponent(() => import('../app-buyer/profile/BuyerAppProfile')),
		BuyerAppContestPage: defineAsyncComponent(() => import('../app-buyer/buyer-view/BuyerAppContestPage')),
		ExpressSpreader: defineAsyncComponent(() => import('../app-buyer/buyer-view/ExpressSpreader.vue')),
		MobileBuyerAppPanel: defineAsyncComponent(() => import('../app-buyer/buyer-view/mobile-buyer-app/MobileBuyerAppPanel')),
		ReviewFormPage: defineAsyncComponent(() => import('./static-page/ReviewFormPage')),
	},
	data() {
		return {
			Module: null as null | string,
			Socket: null,
			StateManager: '',
			ServerManager: new ServerManager(),
			Formatters: new Formatters(),
			Validators,
			Push: null,
			IsLoading: true,
			LoggedUser: {},
			Counters: {},
			IsSaving: false,
			DisplaySavingScreen: true,
			WindowWidth: window.innerWidth,
			Flags: [],
			isDownloadMobileAppTopBarDismissed: window.localStorage.getItem('isDownloadMobileAppTopBarDismissed'),
		};
	},
	computed: {
		...mapWritableState(useAppStore, { moduleFromStore: 'module' }),
		...mapWritableState(useUserStore, ['loggedAs']),
		...mapWritableState(useAppStore, ['websocketUrl']),
		...mapStores(useAppStore),
	},
	watch: {
		IsSaving(n) {
			if (n) {
				this.DisplaySavingScreen = true;
			} else {
				window.setTimeout(() => {
					this.DisplaySavingScreen = false;
				}, 150);
			}
		},
	},
	async created() {
		window.addEventListener('resize', () => { this.WindowWidth = document.body.clientWidth; });
		this.Hello();
		document.body.classList.add('is-preloaded');
		window.addEventListener('message', (el) => {
			const body = document.querySelector('body');
			if (body) {
				if (el.data.signal === 'open') {
					body.style.overflowY = 'hidden';
				} else if (el.data.signal === 'close') {
					body.style.overflowY = 'scroll';
				} else if (el.data.signal === 'download-tos') {
					this.DownloadTOSInModal(el.data.TOSPath, el.data.TOSFileName);
				}
			}
		});

		this.AddNavigationListener();
	},
	methods: {
		...mapActions(useAppStore, ['hasFlag', 'addFlag']),
		...mapActions(useServicesStore, ['setNotifications']),
		...mapActions(useCustomerOnboardingStore, ['canShowOnboarding']),
		AddNavigationListener() {
			const isIframe = window !== window.parent;
			const isRfpForm = document.location.href.indexOf('/formularz/') > -1;

			if (!(isIframe && isRfpForm)) {
				window.addEventListener('popstate', (event) => {
					const { StateManager: stateManager } = this;

					if (event.state && stateManager) {
						if (!Array.isArray(stateManager.InternalStack)) {
							return;
						}

						const vueRoutes = this.$route.path.startsWith('/konto/profil/uslugi-produkty');

						if (vueRoutes && this.CurrentState.Section === 'Services') {
							return;
						}

						if (vueRoutes) {
							this.$router.replace({ name: 'Services' });
						}

						stateManager.InternalStack.pop();
						stateManager.Resolve(
							stateManager.InternalStack[
								stateManager.InternalStack.length - 1
							],
							true,
						);
					}
				});
			}
		},
		ShowTotalChatMsgNotification(value) {
			if (window.localStorage.getItem('isBuyerApp') === 'true') {
				const element = document.querySelector('.navbar-counter[data-type="buyerAppTotalMsg"]');
				if (element) {
					element.innerText = value;
				}
			}
		},
		SubmenuTravel(v) {
			this.Submenu.Travel(v, this);
		},
		async Logout() {
			this.IsLoading = true;
			await fetch(this.loggedAs ? '/j_acegi_exit_user' : '/j_acegi_logout');
			document.location.href = '/';
		},
		async Hello() {
			if (document.location.href.indexOf('/formularz/') > -1) {
				// Ustaw kolor na formularzu - najlepiej przed załadowaniem całości.
				const formStyle = document.createElement('style');
				formStyle.innerHTML = '#preload-placeholder { background: #e97407; } #preload-placeholder .lds-ellipsis div { background: #ffffff; } html, body { overflow: hidden !important; }';
				document.head.appendChild(formStyle);
			}

			const Hello = await this.ServerManager.Hello(
				(window.MODULE || AppModules.MY_OFERTEO) !== AppModules.MY_OFERTEO
					|| window.location.href.indexOf('/konto/zaplac') !== -1
					|| window.location.href.indexOf('/konto/polecanie') !== -1,
			);

			this.Analytics.Create();

			const { href, pathname } = window.location;
			let stateManagerUrl = pathname;

			if (Hello.needsLogin) {
				this.Module = 'LoginRegister';
				stateManagerUrl = '/logowanie';
				if (!document.location.href.endsWith('/konto')) {
					window.localStorage['LoginRedirectTo'] = document.location.href;
				}
			} else {
				this.Module = window.MODULE || AppModules.MY_OFERTEO;
			}

			if (href.indexOf('/buyer-app/') > -1) {
				stateManagerUrl = window.location.pathname;
				this.Module = 'MobileBuyerAppPanel';
			}

			if (href.indexOf('buyer-app-contest') > -1) {
				if (window.localStorage.getItem('isBuyerApp') === 'true') {
					stateManagerUrl = window.location.pathname;
					this.Module = 'BuyerAppContestPage';
				} else if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|appstore.oferteo/i.test(navigator.userAgent)) {
					window.location.href = '/buyer-app/homepage';
				} else {
					window.location.href = '/';
				}
			}

			if (href.includes('rozprowadzarka')) {
				this.Module = 'ExpressSpreader';
			}
			if (href.indexOf('wystaw-opinie') > -1) {
				stateManagerUrl = window.location.pathname;
				this.Module = 'ReviewFormPage';
			}

			// Jeżeli przechodzimy do Mojego Oferteo, ale nie wyspecyfikujemy na którą konkretnie stronę,
			// przejdź na Zapytania lub Kontakty w zależności od tego, czy mamy jakieś nieprzeczytane wiadomości.
			if (['/konto', '/konto/', '/konto/zapytania'].includes(stateManagerUrl)) {
				stateManagerUrl = `/konto/${this.appStore.hasFlag('newMessagesSinceLastVisit') ? 'kontakty' : 'zapytania'}`;
			}

			// Jeżeli jesteśmy na stronie Zapytań bądź Kontaktów, wyświetl Onboarding, o ile jest co pokazać.
			if (this.canShowOnboarding(href)) {
				stateManagerUrl = '/witaj';
			}

			// Utwórz instancję menedżera stanów.
			this.StateManager = new StateManager(stateManagerUrl, this.Module);

			this.LoggedUser = Hello.User;

      if (document.location.href.indexOf('/formularz/') <= 0) {
        const appType = await getAppType();
        this.Push = new PushNotifications(Hello.User.UserId, false, appType, document.location.href);
      }

			this.mobileAppRoutine(Hello);
			this.sendAnalytics();

			this.IsLoading = false;
			this.DisplaySavingScreen = false;

			if (this.$refs.CurrentModule && this.$refs.CurrentModule.OnHello) {
				this.$refs.CurrentModule.OnHello();
			}

			this.moduleFromStore = this.Module;

			if (Hello.servicesNotificationMessage) {
				const servicesStore = useServicesStore();
				servicesStore.setNotifications(Hello.servicesNotificationMessage);
			}
		},

		/**
		 * Procedury wykonywane tylko dla aplikacji mobilnych.
		 */
		mobileAppRoutine(hello) {
			const { userAgent: ua } = navigator;
			const isAppleApp = ua.indexOf('appstore.oferteo') > -1;
			const isApplePhone = ua.match(/iPhone/i);
			const isPwa = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
			const { UserId: userId } = hello.User;

			// Analityka PWA
			if (userId && isPwa && this.Module === AppModules.MY_OFERTEO) {
				serverFetch('/profile/log-pwa-entry.ofer');
			}

			// Wyłącz pokazywanie się paska z prośbą o instalację PWA.
			window.addEventListener('beforeinstallprompt', (e) => { e.preventDefault(); });

			// Wyłącz płatności w aplikacji iOS, jeżeli nie jest ustawiona flaga `enable_ios_payments`
			const shouldDisablePayments = this.hasFlag('enableAppStorePayments');
			if (isAppleApp && shouldDisablePayments) {
				this.addFlag('disableAppStorePayments');
			}

			// Zapisz w localStorage, że jesteśmy w aplikacji iOS
			if (isAppleApp) {
				window.localStorage['IsApp'] = true;
			}

			// Zapisz w localStorage, jeśli przyszliśmy z TWA
			if (document.referrer.includes('android-app://')) {
				window.localStorage['IsApp'] = 'yes';
			}

			// Wyłącz przybliżenie ekranu po kliknięciu w input.
			if (isAppleApp || isApplePhone) {
				const viewportElement = document.querySelector('meta[name="viewport"]');
				viewportElement?.setAttribute(
					'content',
					'width=device-width, initial-scale=1, maximum-scale=1',
				);
			}
		},

		/**
		 * Wysyła informację analityczną zaraz po wejściu na stronę.
		 * Zawiera ona informację o typie urządzenia, status pozwolenia na powiadomienia Push oraz token.
		 */
		sendAnalytics() {
			window.setTimeout(() => {
				const { userAgent } = navigator;
				const isApp = window.localStorage['IsApp'] === 'yes' || userAgent.indexOf('appstore.oferteo') > -1;

				let pushPermission;
				if (isApp) {
					pushPermission = 'granted';
				} else if (typeof Notification !== 'function') {
					pushPermission = 'denied';
				} else {
					pushPermission = Notification.permission;
				}

				if (navigator.userAgent.indexOf('appstore.oferteo') > -1) {
					pushPermission = window.localStorage.CordovaEnableTracking === 'yes' ? 'granted' : 'denied';
				}

				fetch(`/profile/analytics-set-device-type.ofer?deviceType=${getDeviceType()}&pushPermission=${pushPermission}&token=${window.localStorage['notificationToken']}`);
			}, 5000);
		},

    EnablePushNotifications(source = '') {
      this.Push.AskAndSubscribePush(this.Push.UserId);
      if (source && typeof Notification === 'function' && Notification.permission === 'granted') {
        this.Analytics.Gtm(source);
      }
		},
		HideDimmed() {
			document.querySelector('.is-dimmed').style.display = 'none';
		},
		DownloadTOSInModal(url: string, filename: string) {
			const linkElement = document.createElement('a');
			linkElement.href = url;
			linkElement.download = filename;
			document.body.appendChild(linkElement);
			linkElement.click();

			document.body.removeChild(linkElement);
		},
	},
};
</script>
